import { 
  Globe, 
  Shield, 
  HelpCircle, 
  User, 
  LogOut, 
  Search, 
  Settings2,
  X,
  ArrowRight,
  FileText,
  ClipboardList,
  Receipt,
  CreditCard,
  Building2,
  Box,
  BarChart3,
  Layers,
  Calculator,
  PlusCircle,
  Sparkles,
  Command,
  FileSignature,
  Truck,
  ShieldAlert,
  Database
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useCallback, useState, useMemo, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import SignOutModal from './SignOutModal';
import { toast } from 'react-hot-toast';
import clsx from 'clsx';

// SuperAdmin SaaS Platform Module Registry (Strict Scope Isolation)
const SUPERADMIN_MODULES = [
  { title: 'Platform Control Tower', path: '/superadmin/control-tower', category: 'Control Plane', icon: BarChart3, keywords: ['control tower', 'dashboard', 'telemetry', 'overview', 'kpi', 'superadmin', 'platform'] },
  { title: 'Tenant Organizations Directory', path: '/superadmin/clients', category: 'Tenants', icon: Building2, keywords: ['clients', 'tenants', 'organizations', 'companies', 'partitions', 'onboarding'] },
  { title: 'Onboard New Tenant', path: '/superadmin/clients/new', category: 'Quick Action', icon: PlusCircle, keywords: ['create tenant', 'onboard tenant', 'new client', 'provision'] },
  { title: 'Cryptographic Licensing Enclave', path: '/superadmin/licenses', category: 'Security & Enclave', icon: Shield, keywords: ['license', 'licensing', 'rsa', 'rsa-4096', 'keys', 'cryptographic', 'hardware lock'] },
  { title: 'Issue Cryptographic License', path: '/superadmin/licenses', category: 'Quick Action', icon: PlusCircle, keywords: ['issue license', 'generate key', 'new license', 'rsa key'] },
  { title: 'Platform Security & RBAC', path: '/superadmin/roles', category: 'Security', icon: Shield, keywords: ['roles', 'rbac', 'permissions', 'security', 'identities', 'access control'] },
  { title: 'Platform Monitoring & Usage', path: '/superadmin/monitoring', category: 'Telemetry', icon: Sparkles, keywords: ['monitoring', 'telemetry', 'usage', 'metrics', 'health', 'latency', 'cluster'] },
  { title: 'Immutable Audit Trail', path: '/superadmin/audit', category: 'Compliance', icon: FileText, keywords: ['audit', 'audit trail', 'logs', 'compliance', 'security log', 'tamper-evident'] },
  { title: 'Platform Infrastructure & Settings', path: '/superadmin/settings', category: 'Settings', icon: Settings2, keywords: ['settings', 'platform', 'system', 'cluster', 'config'] },
  { title: 'Database Cluster & Snapshots', path: '/superadmin/settings?tab=database', category: 'Infrastructure', icon: Database, keywords: ['database', 'postgres', 'backups', 'snapshots', 'retention'] },
  { title: 'Global SMTP & Mail Relay', path: '/superadmin/settings?tab=smtp', category: 'Infrastructure', icon: Globe, keywords: ['smtp', 'mail', 'email', 'relay', 'gateway', 'notifications'] },
  { title: 'Root Security & Enclave Governance', path: '/superadmin/settings?tab=security', category: 'Security', icon: ShieldAlert, keywords: ['root security', 'enclave', '2fa', 'session timeout', 'passwords'] },
  { title: 'Universal Module Capabilities Mesh', path: '/superadmin/settings?tab=modules', category: 'Architecture', icon: Layers, keywords: ['modules', 'capabilities', 'registry', 'feature flags', 'graph'] },
  { title: 'Platform Operations & Maintenance', path: '/superadmin/settings?tab=maintenance', category: 'Operations', icon: Settings2, keywords: ['maintenance', 'maintenance mode', 'operations', 'heartbeat'] }
];

// Comprehensive ERP Module Registry for Client / Admin Quick Navigation & Search
const CLIENT_ERP_MODULES = [
  { title: 'Executive Command Center', path: '/dashboard', category: 'Executive', icon: BarChart3, keywords: ['executive', 'dashboard', 'command', 'overview', 'kpi', 'metrics'] },
  { title: 'Purchase Orders', path: '/purchase-orders', category: 'Procurement', icon: FileText, keywords: ['po', 'purchase', 'order', 'orders', 'procurement'] },
  { title: 'Create Purchase Order', path: '/purchase-orders/create', category: 'Quick Action', icon: PlusCircle, keywords: ['create po', 'new po', 'new purchase order'] },
  { title: 'Requisitions', path: '/requisitions', category: 'Procurement', icon: ClipboardList, keywords: ['req', 'requisition', 'requisitions', 'pr', 'demand'] },
  { title: 'Create Requisition', path: '/requisitions/create', category: 'Quick Action', icon: PlusCircle, keywords: ['create req', 'new requisition', 'new pr'] },
  { title: 'Sourcing & RFx Events', path: '/rfx', category: 'Procurement', icon: Globe, keywords: ['rfx', 'rfp', 'rfq', 'sourcing', 'bids', 'tender'] },
  { title: 'Goods Receipts (GRN)', path: '/order-receipts', category: 'Supply Chain', icon: Box, keywords: ['grn', 'goods receipt', 'receipts', 'warehouse', 'qc'] },
  { title: 'Approval Center', path: '/approvals', category: 'Workflow', icon: Layers, keywords: ['approval', 'approvals', 'signoff', 'inbox', 'pending'] },
  { title: 'Supplier Quotations', path: '/quotations', category: 'Procurement', icon: FileText, keywords: ['quote', 'quotes', 'quotation', 'quotations', 'bids'] },
  { title: 'Invoices & AP', path: '/finance/accounting/invoices', category: 'Finance', icon: Receipt, keywords: ['invoice', 'invoices', 'ap', 'accounts payable', 'bills'] },
  { title: 'Create Invoice', path: '/finance/accounting/invoices/create', category: 'Quick Action', icon: PlusCircle, keywords: ['create invoice', 'new invoice', 'bill'] },
  { title: 'Payments & Disbursements', path: '/finance/accounting/payments', category: 'Finance', icon: CreditCard, keywords: ['payment', 'payments', 'disbursement', 'payout', 'settlement'] },
  { title: 'Cash & Bank Management', path: '/finance/accounting/cash-bank', category: 'Finance', icon: Building2, keywords: ['cash', 'bank', 'treasury', 'liquidity', 'reconciliation'] },
  { title: 'General Ledger', path: '/finance/accounting/general-ledger', category: 'Finance', icon: Layers, keywords: ['gl', 'general ledger', 'journal', 'ledger', 'accounting'] },
  { title: 'Accounts Receivable (AR)', path: '/finance/accounting/accounts-receivable', category: 'Finance', icon: Receipt, keywords: ['ar', 'accounts receivable', 'billing', 'customer invoice'] },
  { title: 'Tax & Statutory Ledger', path: '/finance/accounting/tax', category: 'Finance', icon: Calculator, keywords: ['tax', 'vat', 'gst', 'statutory', 'liability', 'filing'] },
  { title: 'Budget Overview & Controls', path: '/budget/overview', category: 'Budgets', icon: BarChart3, keywords: ['budget', 'budgets', 'fiscal', 'spend', 'variance', 'cost center'] },
  { title: 'Budget Planning', path: '/budget/planning', category: 'Budgets', icon: BarChart3, keywords: ['budget plan', 'planning', 'allocation'] },
  { title: 'Inventory & Stock Master', path: '/settings/inventory', category: 'Supply Chain', icon: Box, keywords: ['inventory', 'stock', 'sku', 'skus', 'items', 'warehouse'] },
  { title: 'Supplier & Vendor Directory', path: '/settings/vendors', category: 'Master Data', icon: Building2, keywords: ['vendor', 'vendors', 'supplier', 'suppliers', 'directory'] },
  { title: 'Commercial Contracts & MSAs', path: '/settings/contracts', category: 'Master Data', icon: FileSignature, keywords: ['contract', 'contracts', 'agreement', 'agreements', 'msa', 'sla'] },
  { title: 'GRC & Risk Management', path: '/grc', category: 'Governance', icon: Shield, keywords: ['grc', 'risk', 'compliance', 'audit', 'governance', 'sod'] },
  { title: 'Deep Spend Analytics', path: '/analytics', category: 'Analytics', icon: Sparkles, keywords: ['analytics', 'spend', 'bi', 'reports', 'intelligence'] },
  { title: 'System & Master Settings', path: '/settings/system', category: 'Settings', icon: Settings2, keywords: ['settings', 'system settings', 'master', 'system configuration'] },
  { title: 'Business Configuration', path: '/settings/business-configuration', category: 'Settings', icon: Settings2, keywords: ['settings', 'config', 'configuration', 'currency', 'company'] },
  { title: 'User Management', path: '/settings/users', category: 'Settings', icon: User, keywords: ['user', 'users', 'employee', 'access', 'members'] },
  { title: 'Role Permissions & RBAC', path: '/settings/roles', category: 'Settings', icon: Shield, keywords: ['role', 'roles', 'permissions', 'rbac', 'security'] },
  { title: 'Branches & Stores', path: '/settings/branches-stores', category: 'Master Data', icon: Building2, keywords: ['branches', 'stores', 'locations', 'plants'] },
  { title: 'POS Register', path: '/pos/register', category: 'Retail POS', icon: CreditCard, keywords: ['pos', 'register', 'terminal', 'checkout', 'counter'] },
  { title: 'Help & Documentation', path: '/help', category: 'Support', icon: HelpCircle, keywords: ['help', 'support', 'docs', 'tickets', 'guide'] }
];

// Vendor Portal Scope Search Modules (Strict RBAC Isolation)
const VENDOR_MODULES = [
  { title: 'Vendor Dashboard', path: '/vendor/dashboard', category: 'Overview', icon: BarChart3, keywords: ['dashboard', 'vendor', 'overview', 'kpi'] },
  { title: 'Assigned Purchase Orders', path: '/vendor-pos', category: 'Orders', icon: FileText, keywords: ['po', 'purchase orders', 'orders', 'client orders', 'pos'] },
  { title: 'Bids & Tender Opportunities', path: '/vendor/rfx', category: 'Bidding', icon: Globe, keywords: ['rfx', 'rfp', 'rfq', 'bids', 'tenders', 'opportunities'] },
  { title: 'RFx Matching Engine', path: '/vendor/rfx-matching', category: 'Bidding', icon: Sparkles, keywords: ['match', 'rfx matching', 'ai matching', 'recommendations'] },
  { title: 'My Submitted Bids', path: '/vendor/bids', category: 'Bidding', icon: ClipboardList, keywords: ['bids', 'my bids', 'submitted bids', 'quotations'] },
  { title: 'Invoices & Billing', path: '/vendor/finance/invoices', category: 'Finance', icon: Receipt, keywords: ['invoice', 'invoices', 'billing', 'claims'] },
  { title: 'Submit New Invoice', path: '/vendor/finance/invoices/new', category: 'Quick Action', icon: PlusCircle, keywords: ['create invoice', 'new invoice', 'submit invoice'] },
  { title: 'Disbursement & Payments', path: '/vendor/finance/payments', category: 'Finance', icon: CreditCard, keywords: ['payments', 'settlements', 'payouts', 'remittance'] },
  { title: 'Shipments & Deliveries', path: '/vendor/shipments', category: 'Logistics', icon: Truck, keywords: ['shipment', 'shipments', 'dispatch', 'tracking', 'deliveries', 'asn'] },
  { title: 'Company Profile & Compliance', path: '/vendor/profile', category: 'Organization', icon: Building2, keywords: ['profile', 'company', 'bank details', 'kyc', 'tax id', 'organization'] },
  { title: 'Supplier Support & Tickets', path: '/vendor/support', category: 'Support', icon: HelpCircle, keywords: ['support', 'help', 'tickets', 'issues', 'assistance'] }
];

const CLIENT_ONLY_KEYWORDS = [
  'ledger', 'general ledger', 'tax', 'vat', 'gst', 'budget', 'statutory', 
  'approval center', 'workflow', 'roles', 'rbac', 'users', 'requisitions', 'grc', 
  'designations', 'departments', 'business units', 'control tower'
];

export default function Footer({ fullWidth = false }) {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const { user, activePortal, logout, isSuperAdmin } = useAuth();
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchContainerRef = useRef(null);
  const inputRef = useRef(null);

  const isSuperAdminUser = Boolean(
    isSuperAdmin || 
    user?.type === 'SuperAdmin' || 
    user?.email === 'superadmin@sanglob.local' || 
    (Array.isArray(user?.role) && user.role.includes('SuperAdmin')) ||
    (Array.isArray(user?.roles) && user.roles.some(r => (r.name || r) === 'SuperAdmin'))
  );

  const isVendor = !isSuperAdminUser && (activePortal === 'vendor' || user?.type === 'Vendor' || user?.portal_access === 'Vendor Portal');

  // Global Keyboard Shortcut (⌘K / Ctrl+K) to trigger search from anywhere
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close popup when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if search query matches a client-only module while in vendor scope
  const isVendorRestrictedQuery = useMemo(() => {
    if (!isVendor) return false;
    const q = searchQuery.trim().toLowerCase();
    if (!q) return false;
    return CLIENT_ONLY_KEYWORDS.some(k => q.includes(k) || k.includes(q));
  }, [isVendor, searchQuery]);

  // Filter Search Results as per RBAC Scope
  const filteredResults = useMemo(() => {
    const activeModuleRegistry = isSuperAdminUser
      ? SUPERADMIN_MODULES
      : (isVendor ? VENDOR_MODULES : CLIENT_ERP_MODULES);

    const q = searchQuery.trim().toLowerCase();
    if (!q) {
      return activeModuleRegistry.slice(0, 6);
    }
    return activeModuleRegistry.filter((item) => {
      const matchTitle = item.title.toLowerCase().includes(q);
      const matchCategory = item.category.toLowerCase().includes(q);
      const matchKeywords = item.keywords.some((k) => k.includes(q) || q.includes(k));
      return matchTitle || matchCategory || matchKeywords;
    }).slice(0, 8);
  }, [searchQuery, isSuperAdminUser, isVendor]);

  // Handle keyboard navigation in search results
  const handleInputKeyDown = (e) => {
    if (!isOpen && e.key !== 'Escape') {
      setIsOpen(true);
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % (filteredResults.length || 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + filteredResults.length) % (filteredResults.length || 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredResults.length > 0 && filteredResults[selectedIndex]) {
        handleSelect(filteredResults[selectedIndex]);
      }
    }
  };

  const handleSelect = (item) => {
    if (isVendor && !item.path.startsWith('/vendor')) {
      toast.error('Not Authorized: Module is restricted to client internal operations.');
      return;
    }
    setIsOpen(false);
    setSearchQuery('');
    navigate(item.path);
  };

  const handleSignOut = useCallback(() => {
    setShowSignOutConfirm(true);
  }, []);

  const confirmSignOut = useCallback(async () => {
    try {
      setIsSigningOut(true);
      await logout();
    } finally {
      setIsSigningOut(false);
      setShowSignOutConfirm(false);
    }
  }, [logout]);

  return (
    <footer className="bg-white border-t border-slate-200/90 mt-auto relative z-30">
      <div className={fullWidth ? 'w-full px-2 py-2 sm:px-4 lg:px-6' : 'w-full px-4 py-2 sm:px-6 lg:px-8'}>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          
          {/* Left: Branding & Profile */}
          <div className="flex items-center justify-center md:justify-start gap-3.5 text-xs text-slate-500 min-w-0 whitespace-nowrap">
            <span className="font-semibold text-slate-700 truncate">
              © {currentYear} MIREVA <span className="text-slate-400 font-normal">by Sanglob</span>
            </span>
            <span className="text-slate-300">|</span>
            <Link 
              to={isVendor ? "/vendor/profile" : "/profile"} 
              className="flex items-center gap-1 text-slate-600 hover:text-blue-600 font-medium transition-colors shrink-0"
              title="View Account Profile"
            >
              <User className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">{isVendor ? "Supplier Profile" : "Profile"}</span>
            </Link>
          </div>

          {/* Center: Global ERP Omni-Search with Interactive Quick Jump Popover */}
          <div 
            ref={searchContainerRef}
            className="flex items-center justify-center md:flex-1 md:justify-center md:px-4 relative"
          >
            <div className="relative w-full max-w-[540px] md:max-w-[440px]">
              
              {/* Search Icon */}
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
                <Search className="h-3.5 w-3.5" />
              </div>

              {/* Omni-Search Input Field */}
              <input
                ref={inputRef}
                type="text"
                className="block w-full pl-9 pr-16 py-1.5 bg-slate-50 hover:bg-white focus:bg-white border border-slate-200/90 hover:border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-xs transition-all shadow-2xs"
                placeholder={
                  isSuperAdminUser 
                    ? "Search Platform modules, tenants, licensing, telemetry, audit... (⌘K)" 
                    : isVendor 
                    ? "Search Orders, Invoices, Bids, Shipments, Profile... (⌘K)" 
                    : "Global Search POs, Invoices, Requisitions, Vendors... (⌘K)"
                }
                value={searchQuery}
                onFocus={() => setIsOpen(true)}
                onClick={() => setIsOpen(true)}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedIndex(0);
                  setIsOpen(true);
                }}
                onKeyDown={handleInputKeyDown}
              />

              {/* Right Accessories: Shortcut Pill (⌘K) & Clear Button */}
              <div className="absolute inset-y-0 right-0 pr-2 flex items-center gap-1">
                {searchQuery ? (
                  <button
                    type="button"
                    onClick={() => {
                      setSearchQuery('');
                      inputRef.current?.focus();
                    }}
                    className="p-1 rounded text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                ) : (
                  <kbd className="hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-mono font-bold text-slate-400 bg-slate-100 border border-slate-200 rounded shadow-2xs">
                    <Command className="w-2.5 h-2.5" /> K
                  </kbd>
                )}
              </div>

              {/* Interactive Search Popover (Flies upward from footer) */}
              {isOpen && (
                <div className="absolute bottom-full mb-2 left-0 right-0 bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden z-50 animate-in fade-in slide-in-from-bottom-2 duration-150">
                  
                  {/* Popover Header */}
                  <div className="px-3 py-2 bg-slate-50/90 border-b border-slate-100 flex items-center justify-between text-[11px] font-bold text-slate-500">
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="w-3 h-3 text-blue-600" />
                      <span>
                        {isSuperAdminUser 
                          ? 'SuperAdmin Platform Scope Search' 
                          : isVendor 
                          ? 'Vendor Portal Scope Search' 
                          : searchQuery 
                          ? 'Search Results' 
                          : 'Suggested Quick Jumps'}
                      </span>
                    </span>
                    <span className="text-[10px] text-slate-400 font-medium">
                      Press <span className="font-mono font-bold text-slate-600">↑↓</span> to navigate, <span className="font-mono font-bold text-slate-600">↵</span> to select
                    </span>
                  </div>

                  {/* Results List */}
                  <div className="max-h-64 overflow-y-auto stylish-scrollbar p-1.5 space-y-0.5">
                    {isVendorRestrictedQuery && filteredResults.length === 0 ? (
                      <div className="py-5 px-4 text-center">
                        <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center mx-auto mb-1.5 border border-amber-200">
                          <ShieldAlert className="w-4 h-4" />
                        </div>
                        <p className="text-xs font-bold text-amber-800">Not Authorized (Client Management Scope)</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Your supplier account is restricted to vendor portal functions: Orders, Invoices, Bids, Shipments, and Profile.
                        </p>
                      </div>
                    ) : filteredResults.length === 0 ? (
                      <div className="py-6 text-center text-xs text-slate-500">
                        <p className="font-semibold text-slate-700 mb-1">No matching records found</p>
                        <p className="text-[11px] text-slate-400">
                          {isSuperAdminUser 
                            ? 'Try searching for "Tenants", "Licensing", "Monitoring", "Audit", "Database", or "SMTP"' 
                            : isVendor 
                            ? 'Try searching for "Orders", "Invoices", "Bids", "Shipments", or "Profile"' 
                            : 'Try searching for "PO", "Requisition", "Invoice", "Tax", or "Vendors"'}
                        </p>
                      </div>
                    ) : (
                      filteredResults.map((item, idx) => {
                        const Icon = item.icon;
                        const isSelected = idx === selectedIndex;

                        return (
                          <div
                            key={item.path + idx}
                            onClick={() => handleSelect(item)}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            className={clsx(
                              "flex items-center justify-between px-3 py-2 rounded-lg text-xs cursor-pointer transition-all duration-100",
                              isSelected
                                ? "bg-blue-50/90 text-blue-900 font-semibold"
                                : "text-slate-700 hover:bg-slate-50"
                            )}
                          >
                            <div className="flex items-center gap-2.5 min-w-0">
                              <div className={clsx(
                                "w-6 h-6 rounded-md flex items-center justify-center shrink-0 transition-colors",
                                isSelected ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600"
                              )}>
                                <Icon className="w-3.5 h-3.5" />
                              </div>
                              <div className="min-w-0">
                                <span className="block truncate font-medium">
                                  {item.title}
                                </span>
                              </div>
                            </div>

                            <div className="flex items-center gap-2 shrink-0">
                              <span className={clsx(
                                "text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase tracking-wider",
                                isSelected
                                  ? "bg-blue-100 text-blue-700 border-blue-200"
                                  : "bg-slate-100 text-slate-500 border-slate-200"
                              )}>
                                {item.category}
                              </span>
                              <ArrowRight className={clsx(
                                "w-3 h-3 transition-transform",
                                isSelected ? "text-blue-600 translate-x-0.5" : "text-slate-300"
                              )} />
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                </div>
              )}
            </div>
          </div>

          {/* Right: Quick Links & Sign Out Action */}
          <div className="flex items-center justify-center md:justify-end gap-3 text-xs text-slate-500 shrink-0 pr-36 sm:pr-44">
            <Link 
              to="/help" 
              className="text-slate-600 hover:text-blue-600 transition-colors"
            >
              Help
            </Link>
            <span className="text-slate-300">|</span>
            <Link 
              to="/privacy-policy" 
              className="text-slate-600 hover:text-blue-600 transition-colors"
            >
              Privacy
            </Link>
            <span className="text-slate-300">|</span>
            <Link 
              to="/terms-of-service" 
              className="text-slate-600 hover:text-blue-600 transition-colors"
            >
              Terms
            </Link>
            <span className="text-slate-300">|</span>
            <button
              type="button"
              onClick={handleSignOut}
              className="flex items-center gap-1 text-slate-600 hover:text-rose-600 font-medium transition-colors cursor-pointer"
              title="Sign Out"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out</span>
            </button>
          </div>

        </div>
      </div>

      {/* Modern Executive Modal for Sign Out */}
      <SignOutModal
        isOpen={showSignOutConfirm}
        onClose={() => setShowSignOutConfirm(false)}
        onConfirm={confirmSignOut}
        isSigningOut={isSigningOut}
      />
    </footer>
  );
}
